import { ATTRIBUTES, type Attribute } from "./attributes.ts";
import type { AttributeKnobs } from "./attribute-rules.ts";
import type { BackgroundId, Rules } from "./types.ts";

/**
 * Spending attribute points and the respec (ADR-138's amendment of 2026-10-31, decisions 4, 6 and 7): what a batch of
 * spends or a respec does to a character's held values, as pure whole-number data. The map server decides with these
 * before its store commits; the page previews through `derivedStatsOf`, the clamp the server's effective values take.
 */

/**
 * The level from which a respec is no longer offered: below it a respec is a free full reset; at and above it the page
 * says a respec comes later at a Shrine of Attunement (ADR-138's note of 2026-10-31, P2.22-T44).
 */
export const ATTRIBUTE_RESPEC_LEVEL = 10;

/**
 * The most unspent points a character holds: what the `attribute_points` column allows (migration 052).
 */
export const MAX_ATTRIBUTE_POINTS = 999;

/**
 * The most points one entry of a batch spends.
 */
export const MAX_SPEND_POINTS = 10;

/**
 * Why an attribute request is refused, as `attributeRefused` carries it: `none`, more points asked than held; `cap`, a
 * value would pass `attribute_cap`; `combat`, the character is in combat; `level`, a respec at or above
 * `ATTRIBUTE_RESPEC_LEVEL`; `spent`, a respec with nothing spent; `busy`, another attribute operation is in flight, the
 * stored row moved under it, the request came past the rate window or the server could not take it; `frozen`, a Game
 * Master holds the character; `points`, a respec would leave more than `MAX_ATTRIBUTE_POINTS` unspent.
 */
export const ATTRIBUTE_REFUSALS = Object.freeze(["none", "cap", "combat", "level", "spent", "busy", "frozen", "points"] as const);

/**
 * One reason of `ATTRIBUTE_REFUSALS`.
 */
export type AttributeRefusal = (typeof ATTRIBUTE_REFUSALS)[number];

/**
 * Whether a respec is offered at a level: `free` below `ATTRIBUTE_RESPEC_LEVEL`, `shrine` (not yet) at and above it.
 */
export type RespecKind = "free" | "shrine";

/**
 * One entry of a spend batch: the attribute and the points it takes.
 */
export type AttributeSpend = readonly [Attribute, number];

/**
 * A character's attributes as the server holds them: the seven stored values in `ATTRIBUTES` order and the points left.
 */
export interface HeldAttributes {
  readonly stored: readonly number[];
  readonly points: number;
}

/**
 * What an accepted operation leaves: the new stored values, the points left and the signed change of each value, which
 * the ledger row records.
 */
export interface AttributeChange {
  readonly kind: "ok";
  readonly stored: readonly number[];
  readonly points: number;
  readonly deltas: readonly number[];
}

/**
 * An operation refused, with nothing applied.
 */
export interface AttributeRefused<R extends AttributeRefusal> {
  readonly kind: "refused";
  readonly reason: R;
}

/**
 * What a batch of spends does: every entry applied, or nothing.
 *
 * @param held The held values and points.
 * @param spends The batch: 1 to 7 entries, each attribute once, each 1 to `MAX_SPEND_POINTS` points.
 * @param knobs The attribute knobs, for the cap.
 * @returns The change, or `none` when the batch asks more than the points held, or `cap` when a value would pass the cap.
 * @throws RangeError when the batch is empty, longer than seven, repeats an attribute or holds points out of range: a
 *   batch the schema refuses before it reaches here.
 */
export function spendOutcome(held: HeldAttributes, spends: readonly AttributeSpend[], knobs: AttributeKnobs): AttributeChange | AttributeRefused<"none" | "cap"> {
  if (spends.length === 0 || spends.length > ATTRIBUTES.length) {
    throw new RangeError(`a spend batch holds 1 to ${ATTRIBUTES.length} entries, not ${spends.length}`);
  }
  const deltas = ATTRIBUTES.map(() => 0);
  let total = 0;
  for (const [attribute, points] of spends) {
    const index = ATTRIBUTES.indexOf(attribute);
    if (index < 0 || deltas[index] !== 0 || !Number.isInteger(points) || points < 1 || points > MAX_SPEND_POINTS) {
      throw new RangeError(`a spend entry is an attribute once with 1 to ${MAX_SPEND_POINTS} points, not ${attribute} ${points}`);
    }
    deltas[index] = points;
    total += points;
  }
  if (total > held.points) {
    return { kind: "refused", reason: "none" };
  }
  const stored = held.stored.map((value, index) => value + (deltas[index] as number));
  if (stored.some((value, index) => (deltas[index] as number) > 0 && value > knobs.cap)) {
    return { kind: "refused", reason: "cap" };
  }
  return { kind: "ok", stored, points: held.points - total, deltas };
}

/**
 * The values a respec returns a character to: `attribute_start` in each, plus its background's starting points.
 *
 * @param background The character's background, or null for none.
 * @param rules The rules: the start and the backgrounds table.
 * @returns The seven values in `ATTRIBUTES` order.
 */
export function respecBase(background: BackgroundId | null, rules: Pick<Rules, "constants" | "tables">): number[] {
  const starting = background === null ? undefined : rules.tables.backgrounds.find((row) => row.id === background)?.startingAttributes;
  return ATTRIBUTES.map((attribute) => rules.constants.attribute_start + (starting?.[attribute] ?? 0));
}

/**
 * Whether a respec is offered at a level.
 *
 * @param level The character's level.
 * @returns `free` below `ATTRIBUTE_RESPEC_LEVEL`, `shrine` at and above it.
 */
export function respecKind(level: number): RespecKind {
  return level < ATTRIBUTE_RESPEC_LEVEL ? "free" : "shrine";
}

/**
 * What a respec does: every value back to its base and every point spent above it back to the points; a value found
 * below its base is raised to it and gives nothing.
 *
 * @param held The held values and points.
 * @param base The values to return to (`respecBase`).
 * @param level The character's level.
 * @returns The change, or `level` at or above `ATTRIBUTE_RESPEC_LEVEL`, `spent` when no value stands above its base, or
 *   `points` when the points returned would pass `MAX_ATTRIBUTE_POINTS`.
 */
export function respecOutcome(held: HeldAttributes, base: readonly number[], level: number): AttributeChange | AttributeRefused<"level" | "spent" | "points"> {
  if (respecKind(level) !== "free") {
    return { kind: "refused", reason: "level" };
  }
  let returned = 0;
  const deltas = held.stored.map((value, index) => {
    const floor = base[index] as number;
    returned += Math.max(0, value - floor);
    return floor - value;
  });
  if (returned === 0) {
    return { kind: "refused", reason: "spent" };
  }
  if (held.points + returned > MAX_ATTRIBUTE_POINTS) {
    return { kind: "refused", reason: "points" };
  }
  return { kind: "ok", stored: [...base], points: held.points + returned, deltas };
}
